import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useCabinet } from "./context.js";

/**
 * Куда ведёт «Назад» из шапки. Корневые вкладки (календарь, диалоги, клиенты, «Ещё») идут без кнопки.
 * Мастер первой настройки тоже без шапки: там своя «Назад» внизу, рядом с «Далее».
 * `billingIsTab`: у админа подписка сама в нижней панели.
 * `wide`: на широком экране открытый диалог показан рядом со списком, «Назад» к списку не нужна.
 */
export function headerBackTo(
  pathname: string,
  base: string,
  opts: { isOwner: boolean; ownerKnown: boolean; billingIsTab: boolean; wide?: boolean },
): string | null {
  const prefix = `${base}/`;
  if (!pathname.startsWith(prefix)) return null;
  const rest = pathname.slice(prefix.length).replace(/\/$/, "");
  if (/^settings\/masters\/[^/]+$/.test(rest)) {
    if (!opts.ownerKnown) return null;
    return opts.isOwner ? "settings/masters" : "settings";
  }
  if (rest.startsWith("settings/")) return "settings";
  if (rest.startsWith("conversations/")) return opts.wide ? null : "conversations";
  if (rest.startsWith("clients/")) return "clients";
  if (rest === "demo" || rest === "reports") return "settings";
  if (rest === "billing" && !opts.billingIsTab) return "settings";
  return null;
}

/** «‹ Назад» к разделу «Ещё» (или к указанному пути внутри кабинета). `onClick` может отменить переход (`preventDefault`). */
export function BackLink({
  to = "settings",
  label,
  className,
  onClick,
}: {
  to?: string;
  label?: string;
  className?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}) {
  const { t } = useTranslation();
  const { base } = useCabinet();
  return (
    <Link to={`${base}/${to}`} onClick={onClick} className={className ?? "mb-2 inline-block py-1 text-sm text-slate-500"}>
      ‹ {label ?? t("common.back")}
    </Link>
  );
}
